import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  cancelRefundStatusById,
  editRefundStatusById,
} from '../../../Store/Actions/events';
import {FETCH_ERROR} from '../../../Constant/ActionTypes';
import {resetMemorizedInitiatedRefundAttendees} from '../../../Store/Actions/eventRegister';
import {getRegistrationProcessingFee, roundCurrency} from '../../../Util/commonUtility';

// REVERT: the processing fee is no longer withheld from refunds, so the previous
// PROCESSING_FEE_RATE / REFUNDABLE_RATE (0.971) constants are removed. See computeRefundableBase.

const sum = (arr) => arr.reduce((total, num) => total + num, 0);

const sumProcessingFees = (rows) =>
  roundCurrency((rows || []).reduce((total, item) => total + getRegistrationProcessingFee(item), 0));

const sumPaidAmountWithProcessingFee = (rows) => {
  const paidRegistrationFee = sum(
    (rows || []).map((item) => Number(item?.paid_amount || 0)),
  );
  return roundCurrency(paidRegistrationFee + sumProcessingFees(rows));
};

const uniqueStripePaymentIds = (rows) =>
  Array.from(
    new Set(
      (rows || [])
        .map((item) => item?.transaction_id)
        .filter((id) => Boolean(id)),
    ),
  );

// REVERT: the processing-fee cap on refunds is removed. Refundable base per attendee:
//   - fee added by attendee (is_processing_fee_added) -> base + the processing fee they paid on
//     top is refundable (e.g. 100 base + 2.9% => 102.9).
//   - fee absorbed by org                             -> the whole base is refundable (the 2.9%
//     is no longer withheld).
// Mixed selections are summed per attendee so the preview matches the API's feeAdjustedRefundableBase.
const computeRefundableBase = (rows) =>
  (rows || []).reduce((total, item) => {
    const base = Number(item?.total_cost) - Number(item?.discount_amount || 0);
    if (item?.is_processing_fee_added) {
      return total + base + getRegistrationProcessingFee(item);
    }
    return total + base;
  }, 0);

/**
 * Shared state + handlers behind the admin "approve refund" flow used on both the
 * per-event refund page (EventRefund) and the cross-event refund page (AllRefund).
 *
 * Set `filterByLocalChapter: true` on the all-events page so a mixed-chapter selection
 * gets constrained to a single chapter at a time (each chapter has its own currency and
 * the refundable-base math needs a single, consistent chapter). The per-event page never
 * mixes chapters, so it can leave this off.
 */
const useRefundApproval = ({filterByLocalChapter = false} = {}) => {
  const dispatch = useDispatch();

  const [selectedRefundAttendees, setSelectedRefundAttendees] = useState();
  const [percentageRefund, setPercentageRefund] = useState('');
  const [refundMode, setRefundMode] = useState('percentage');
  const [exactAmountRefund, setExactAmountRefund] = useState('');
  const [showApproveReject, setShowApproveReject] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowTotalCost, setSelectedRowTotalCost] = useState();
  const [selectedRowProcessingFee, setSelectedRowProcessingFee] = useState(0);
  const [
    selectedRowProcessingFeePercentage,
    setSelectedRowProcessingFeePercentage,
  ] = useState(0);
  const [selectedRowTotalRefundAmount, setSelectedRowTotalRefundAmount] =
    useState(0);
  const [selectedRowPaidAmount, setSelectedRowPaidAmount] = useState(0);
  const [selectedRowPaidAmountWithFee, setSelectedRowPaidAmountWithFee] =
    useState(0);
  const [selectedRowStripePaymentIds, setSelectedRowStripePaymentIds] =
    useState([]);
  const [selectedRowRefundableBase, setSelectedRowRefundableBase] = useState(0);
  const [selectedRowCreditAmount, setSelectedRowCreditAmount] = useState(0);
  const [selectedRowTotalDiscount, setSelectedRowTotalDiscount] = useState(0);
  const [selectedRowLocalChapterId, setSelectedRowLocalChapterId] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [refundToAccount, setRefundToAccount] = useState(false);
  // True while an approve-refund request is in flight; blocks repeat submissions.
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  // Ref mirror so a second click before the re-render is still blocked.
  const refundSubmittingRef = useRef(false);

  const selectedInitiatedRefundIds = useSelector(
    (state) => state.eventRegister.selectedInitiatedRefundIds,
  );

  useEffect(() => {
    if (selectedInitiatedRefundIds) {
      setSelectedRowKeys(
        selectedInitiatedRefundIds?.map((row) => Number(row?.id)),
      );
      const totalCreditAmount = sum(
        selectedInitiatedRefundIds.map((item) => Number(item?.credit_amount)),
      );
      const totalPaidAmount = sum(
        selectedInitiatedRefundIds.map((item) => Number(item?.paid_amount)),
      );
      const totalCost = sum(
        selectedInitiatedRefundIds.map((item) => Number(item?.total_cost)),
      );
      const totalDiscountAmount = sum(
        selectedInitiatedRefundIds.map((item) => Number(item?.discount_amount)),
      );
      const totalProcessingFeeAmount = sumProcessingFees(selectedInitiatedRefundIds);

      setSelectedRowCreditAmount(totalCreditAmount);
      setSelectedRowPaidAmount(totalPaidAmount);
      setSelectedRowPaidAmountWithFee(
        sumPaidAmountWithProcessingFee(selectedInitiatedRefundIds),
      );
      setSelectedRowStripePaymentIds(
        uniqueStripePaymentIds(selectedInitiatedRefundIds),
      );
      setSelectedRowTotalCost(totalCost - totalDiscountAmount);
      setSelectedRowRefundableBase(
        computeRefundableBase(selectedInitiatedRefundIds),
      );
      const processingFeePercentage =
        Number(selectedInitiatedRefundIds?.[0]?.processing_fee_percentage) || 0;
      setSelectedRowProcessingFee(totalProcessingFeeAmount);
      setSelectedRowProcessingFeePercentage(processingFeePercentage);
      dispatch(resetMemorizedInitiatedRefundAttendees());
    }
  }, [selectedInitiatedRefundIds]);

  // Credit-only selections have no card-paid amount to reverse via Stripe.
  const isCreditOnlyRefund =
    Number(selectedRowPaidAmount || 0) <= 0 &&
    Number(selectedRowCreditAmount || 0) > 0;

  const resetRefundInputs = () => {
    setPercentageRefund('');
    setExactAmountRefund('');
    setRefundMode('percentage');
    setRefundToAccount(false);
    setIsModalVisible(false);
    setSelectedRowTotalRefundAmount(0);
  };

  const handleApproveRefunds = () => {
    if (refundSubmittingRef.current) {
      return;
    }
    if (!selectedRefundAttendees || selectedRefundAttendees.length <= 0) {
      dispatch({
        type: FETCH_ERROR,
        payload: 'Please select attendees to approve refund',
      });
      return;
    }

    const Data = {
      eventRegistrationIds: selectedRefundAttendees,
      status: 0,
      // Credit-only payments must return to account credit (no card charge to reverse).
      refund_to_account: isCreditOnlyRefund ? true : refundToAccount,
    };

    if (refundMode === 'amount') {
      if (exactAmountRefund === '' || Number(exactAmountRefund) <= 0) {
        dispatch({
          type: FETCH_ERROR,
          payload: 'Please enter a refund amount greater than zero',
        });
        return;
      }
      Data.refund_amount = Number(exactAmountRefund);
    } else {
      if (percentageRefund === '') {
        dispatch({
          type: FETCH_ERROR,
          payload: 'Please enter a refund percentage',
        });
        return;
      }
      if (Number(percentageRefund) <= 0) {
        dispatch({
          type: FETCH_ERROR,
          payload: 'Refund percentage cannot be zero',
        });
        return;
      }
      Data.refund_percentage = Number(percentageRefund);
    }

    refundSubmittingRef.current = true;
    setRefundSubmitting(true);
    Promise.resolve(
      dispatch(
        editRefundStatusById(
          Data,
          setSelectedRowKeys,
          setShowApproveReject,
          resetRefundInputs,
        ),
      ),
    ).finally(() => {
      refundSubmittingRef.current = false;
      setRefundSubmitting(false);
    });
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value >= 0 && value <= 100) {
      setPercentageRefund(value);
      // Refundable base already accounts for the processing fee per attendee (full base when
      // the attendee bore the fee, 97.1% when the org absorbed it). Keep in sync with the API
      // (refundByEventRegistration.ts refundableRate).
      const refundAmount =
        Math.round(value * Number(selectedRowRefundableBase)) / 100;
      setSelectedRowTotalRefundAmount(refundAmount);
    }
  };

  const handleRefundAmountChange = (e) => {
    const value = e.target.value;
    // Allow only non-negative numeric input (up to 2 decimal places)
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setExactAmountRefund(value);
      // Exact amount is the TOTAL to refund, split equally across the selected
      // attendees, capped at the fee-adjusted refundable base so the non-refundable
      // processing fee stays withheld — matching the API's feeAdjustedRefundableBase
      // (refundByEventRegistration.ts) and the percentage mode above.
      const refundAmount = Math.min(
        Number(value) || 0,
        Number(selectedRowRefundableBase),
      );
      setSelectedRowTotalRefundAmount(refundAmount);
    }
  };

  const handleRefundModeChange = (e) => {
    const mode = e.target.value;
    setRefundMode(mode);
    // Reset the inactive input and preview when switching modes.
    setPercentageRefund('');
    setExactAmountRefund('');
    setSelectedRowTotalRefundAmount(0);
  };

  const handleRadioChange = (e) => {
    if (e.target.value === 'direct') {
      setRefundToAccount(false);
    } else if (e.target.value === 'credit') {
      setRefundToAccount(true);
    }
  };

  const handleRefundVisible = () => {
    if (isCreditOnlyRefund) {
      setRefundToAccount(true);
    }
    setIsModalVisible(true);
  };

  const handleCancelRefund = () => {
    const Data = {
      eventRegistrationIds: selectedRefundAttendees,
      status: 2,
    };
    dispatch(cancelRefundStatusById(Data, setSelectedRowKeys));
  };

  const hideModal = () => {
    if (refundSubmittingRef.current) {
      return;
    }
    resetRefundInputs();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (_selectedRowIds, selectedKeys) => {
      const nonRefundedSelectedKeys = selectedKeys?.filter(
        (row) => row?.status !== 'Payment Refunded',
      );

      let filterSelectedKeys = nonRefundedSelectedKeys;
      let selectedChapterId = selectedRowLocalChapterId;

      if (filterByLocalChapter) {
        selectedChapterId =
          selectedRowLocalChapterId ||
          nonRefundedSelectedKeys?.[0]?.local_chapter_id;
        filterSelectedKeys = nonRefundedSelectedKeys?.filter(
          (row) => row?.local_chapter_id === selectedChapterId,
        );
      }

      const totalCost = sum(
        filterSelectedKeys.map((item) => Number(item?.total_cost)),
      );
      const totalDiscountAmount = sum(
        filterSelectedKeys.map((item) => Number(item?.discount_amount)),
      );
      const totalCreditAmount = sum(
        filterSelectedKeys.map((item) => Number(item?.credit_amount)),
      );
      const totalPaidAmount = sum(
        filterSelectedKeys.map((item) => Number(item?.paid_amount)),
      );
      const totalProcessingFeeAmount = sumProcessingFees(filterSelectedKeys);

      const processingFeePercentage =
        Number(filterSelectedKeys?.[0]?.processing_fee_percentage) || 0;

      setSelectedRowCreditAmount(totalCreditAmount);
      setSelectedRowPaidAmount(totalPaidAmount);
      setSelectedRowPaidAmountWithFee(
        sumPaidAmountWithProcessingFee(filterSelectedKeys),
      );
      setSelectedRowStripePaymentIds(uniqueStripePaymentIds(filterSelectedKeys));
      setSelectedRowTotalDiscount(totalDiscountAmount);
      setSelectedRowTotalCost(totalCost - totalDiscountAmount);
      setSelectedRowRefundableBase(computeRefundableBase(filterSelectedKeys));
      setSelectedRowProcessingFee(totalProcessingFeeAmount);
      setSelectedRowProcessingFeePercentage(processingFeePercentage);
      if (filterByLocalChapter) {
        setSelectedRowLocalChapterId(selectedChapterId);
      }

      setSelectedRowKeys(filterSelectedKeys?.map((row) => row.id));
    },
    getCheckboxProps: (record) => {
      return {
        disabled:
          record.status == 'Payment Refunded' ||
          (filterByLocalChapter &&
            selectedRowLocalChapterId &&
            record.local_chapter_id !== selectedRowLocalChapterId),
      };
    },
  };

  useEffect(() => {
    if (selectedRowKeys?.length > 0) {
      setShowApproveReject(true);
      setSelectedRefundAttendees(selectedRowKeys);
    } else {
      setShowApproveReject(false);
      setSelectedRefundAttendees([]);
      if (filterByLocalChapter) {
        setSelectedRowLocalChapterId(undefined);
      }
    }
  }, [selectedRowKeys]);

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    selectedRefundAttendees,
    showApproveReject,
    isModalVisible,
    refundToAccount,
    setRefundToAccount,
    percentageRefund,
    refundMode,
    exactAmountRefund,
    selectedRowTotalCost,
    selectedRowTotalRefundAmount,
    selectedRowCreditAmount,
    selectedRowPaidAmount,
    selectedRowPaidAmountWithFee,
    selectedRowStripePaymentIds,
    selectedRowTotalDiscount,
    selectedRowLocalChapterId,
    isCreditOnlyRefund,
    rowSelection,
    handleRadioChange,
    handleRefundVisible,
    handleCancelRefund,
    hideModal,
    handleApproveRefunds,
    refundSubmitting,
    handleInputChange,
    handleRefundAmountChange,
    handleRefundModeChange,
    selectedRowProcessingFee,
    selectedRowProcessingFeePercentage,
  };
};

export default useRefundApproval;
